import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useGetQuestionsFeedInfiniteQuery, useAnswerQuestionMutation } from '../../apis';
import { Spinner, SpinnerVariants } from '../../UI';
import { getToken } from '../../utils/auth';
import { markOnboarded } from '../../utils/onboarding';
import type { Question } from '../../types';

// How many starter questions to offer. Soulmate matching needs 10+ shared
// answers, so a new account starts invisible to the feature. The welcome quiz
// front-loads a handful of votes right after signup so Soulmates has signal to
// work with from day one — the fix for the cold-start the empty state admits to.
const QUIZ_SIZE = 8;

export const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const loggedIn = !!getToken();

  // The plain /questions list was removed with the feed-dedupe, so draw the
  // starter questions from the first page(s) of the shared feed query. One page
  // (FEED_PAGE_SIZE) is far more than QUIZ_SIZE, so no pagination is needed here.
  const { data, isLoading, isError, refetch } = useGetQuestionsFeedInfiniteQuery(undefined, {
    // Guests can't vote, so don't even fetch for them (the guard below redirects).
    skip: !loggedIn,
  });
  // undefined while the first page is loading; an array (possibly empty) after.
  const questions = data?.pages.flat();
  const [answerQuestion] = useAnswerQuestionMutation();

  // Freeze the quiz set once, the first time questions arrive: the user's
  // unanswered ones, capped at QUIZ_SIZE. Crucially this is NOT derived from
  // every response — each vote invalidates `Questions` and refetches with the
  // just-voted question now marked answered, which would otherwise reshuffle
  // the list out from under the current index (skipping questions, or indexing
  // past the end and crashing). null = not captured yet.
  const [quiz, setQuiz] = useState<Question[] | null>(null);
  useEffect(() => {
    if (quiz === null && questions) {
      setQuiz(questions.filter((q) => q.answered === null).slice(0, QUIZ_SIZE));
    }
  }, [questions, quiz]);

  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);

  // Guests have nothing to do here.
  if (!loggedIn) {
    return <Navigate to="/" replace />;
  }

  const finish = () => {
    markOnboarded();
    setDone(true);
  };

  const skip = () => {
    markOnboarded();
    navigate('/');
  };

  const handleAnswer = (answer: boolean) => {
    if (!quiz) return;
    const current = quiz[index];
    if (current) {
      // Fire-and-forget: a failed vote shouldn't stall onboarding. The feed will
      // reflect the true state later; here we optimistically move on.
      answerQuestion({ questionId: current.questionId, answer })
        .unwrap()
        .catch(() => {
          /* swallow — keep the quiz snappy */
        });
    }
    if (index + 1 >= quiz.length) {
      finish();
    } else {
      setIndex((i) => i + 1);
    }
  };

  // Fetch failed BEFORE we captured a quiz — don't silently treat it as
  // "nothing to answer" and send the user to the success screen. Offer a retry
  // and a way past it. Once the quiz is frozen (quiz !== null), ignore later
  // refetch errors: each vote invalidates `Questions` and can fail in the
  // background, but we already have everything needed to finish the quiz.
  if (isError && quiz === null) {
    return (
      <div className="text-white max-w-app-middle-side mx-auto w-full px-app-base py-app-2xl text-center">
        <h1 className="text-app-xl font-bold">Couldn&apos;t load questions</h1>
        <p className="text-app-gray-light mt-app-base">
          Something went wrong fetching your starter questions.
        </p>
        <div className="mt-app-2xl flex flex-col gap-app-base">
          <button
            onClick={() => refetch()}
            className="bg-app-primary text-black font-semibold px-app-lg py-app-sm rounded-app-sm"
          >
            Try again
          </button>
          <button onClick={skip} className="text-app-gray hover:text-white">
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || quiz === null) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Spinner variant={SpinnerVariants.CIRCLE} />
      </div>
    );
  }

  // Completion (also the path when there were no questions to show).
  if (done || quiz.length === 0) {
    const answered = done ? quiz.length : 0;
    return (
      <div className="text-white max-w-app-middle-side mx-auto w-full px-app-base py-app-2xl text-center">
        <div className="text-6xl mb-app-lg">🎉</div>
        <h1 className="text-app-2xl font-bold">You&apos;re all set</h1>
        <p className="text-app-gray-light mt-app-base leading-relaxed">
          {answered > 0
            ? `You shared ${answered} opinion${answered === 1 ? '' : 's'}. The more you vote, the sharper your Soulmates get — people who answer the way you do.`
            : 'Jump in and start voting — the more you answer, the better Pinion gets at finding the people who think like you.'}
        </p>
        <div className="mt-app-2xl flex flex-col gap-app-base">
          <Link
            to="/soulmates"
            className="bg-app-primary text-black font-semibold px-app-lg py-app-sm rounded-app-sm"
          >
            See my Soulmates
          </Link>
          <Link
            to="/"
            className="border border-app-gray text-white px-app-lg py-app-sm rounded-app-sm hover:border-app-white"
          >
            Explore Pinion
          </Link>
        </div>
      </div>
    );
  }

  const current = quiz[index];
  const progress = Math.round((index / quiz.length) * 100);

  return (
    <div className="text-white max-w-app-middle-side mx-auto w-full px-app-base py-app-xl flex flex-col min-h-[70vh]">
      <div className="flex items-center justify-between mb-app-lg">
        <h1 className="text-app-lg font-bold">
          Welcome to <span className="text-app-primary">Pinion</span> 👋
        </h1>
        <button onClick={skip} className="text-app-sm text-app-gray hover:text-white">
          Skip for now
        </button>
      </div>

      <p className="text-app-gray-light">
        A few quick opinions to get you started — tap Yes or No.
      </p>

      {/* Progress */}
      <div className="mt-app-base">
        <div className="h-1.5 w-full rounded-app-xs bg-app-third overflow-hidden">
          <div
            className="h-full bg-app-primary transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="text-app-sm text-app-gray mt-app-xs">
          Question {index + 1} of {quiz.length}
        </div>
      </div>

      {/* The question */}
      <div className="flex-1 flex items-center justify-center py-app-xl">
        <div className="text-app-2xl font-bold text-center leading-snug">{current.title}</div>
      </div>

      {/* Yes / No */}
      <div className="flex gap-app-base">
        <button
          onClick={() => handleAnswer(true)}
          className="flex-1 py-app-base rounded-app-base bg-app-primary text-black font-semibold text-app-lg"
        >
          Yes
        </button>
        <button
          onClick={() => handleAnswer(false)}
          className="flex-1 py-app-base rounded-app-base bg-app-secondary text-white font-semibold text-app-lg"
        >
          No
        </button>
      </div>
    </div>
  );
};
